import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Image from '../Image/Image';
import { dt } from '../../helpers/Utils/GetTailwindToken';

/**
 * Banner component to standardize the styling and html structure of any advertisement/promotion image banner component on the page
 * Currently it support responsive layout occupying 100% width of wrapper HTML element.
 *
 */

class Banner extends Component {
    /**
     * Supported React properties
     * @type {Object}
     */

    static propTypes = {
        /**
         * URL of the related page. Once banner is clicked, it should take the user to the related page.
         * @type {string}
         */
        href: PropTypes.string.isRequired,

        /**
         * source path of the banner image
         * @type {string}
         */
        bannerImageUrl: PropTypes.string.isRequired,

        /**
         * alternative text for the banner image
         * @type {string}
         */
        altText: PropTypes.string.isRequired,

        /**
         * The target prop specifies where to open the linked document.
         * Possible values are _blank, _self, _parent, _top
         * @type {string}
         */
        target: PropTypes.string.isRequired,

        /**
         * The aria-label attribute is used to define a string that labels the current element.
         * @type {string}
         */
        ariaLabel: PropTypes.string.isRequired,

        /**
         * The adobeAudience attribute is used to define a string that labels the audience for analytics.
         * @type {string}
         */
        adobeAudience: PropTypes.string,

        /**
         * Unique name for referencing dom element in automation testing
         * @type {String}
         */
        automationId: PropTypes.string,

        /**
         * It is a flag indicating to go for soft routing or hard routing
         * @type {String}
         */
        softRoute: PropTypes.bool,

        /**
         * CSS className for root element
         * @type {String}
         */
        bannerClass: PropTypes.string,
        analyticsTag: PropTypes.string,
        onImageLoad: PropTypes.func,
        onImageError: PropTypes.func,
        bannerImageClass: PropTypes.string,
        width: PropTypes.string,
        height: PropTypes.string,
        plccId: PropTypes.string,
        plccName: PropTypes.string,
    };

    static defaultProps = {
        adobeAudience: '',
        analyticsTag: '',
        automationId: 'bnrhspot',
        bannerClass: '',
        bannerImageClass: '',
        height: '',
        href: '',
        onImageError: () => {},
        onImageLoad: () => {},
        softRoute: false,
        target: '',
        width: '',
        plccId: '',
        plccName: '',
    };

    linkCreation() {
        const {
            adobeAudience,
            altText,
            analyticsTag,
            ariaLabel,
            bannerImageClass,
            bannerImageUrl,
            height,
            onImageError,
            onImageLoad,
            softRoute,
            target,
            width,
            plccId,
            plccName,
        } = this.props;

        const imgTag = (
            <Image
                imageRatio={0}
                width={width}
                height={height}
                imgClass={dt(['!w-full', 'h-full', `${bannerImageClass}`])}
                src={bannerImageUrl}
                alt={altText}
                title={altText}
                automationId="banner-image"
                onImageLoad={onImageLoad}
                onImageError={onImageError}
            />
        );
        let redirectTag = null;
        let softRouteURL = this.props.href;
        /* istanbul ignore next */
        if (softRouteURL) {
            const matchForCmRe = /cm_re/.test(softRouteURL.toLowerCase());
            if (!matchForCmRe && analyticsTag) {
                const match = /.+\?.*/.test(softRouteURL);
                if (match) {
                    softRouteURL += `&${analyticsTag}`;
                } else {
                    softRouteURL += `?${analyticsTag}`;
                }
            } else if (adobeAudience) {
                softRouteURL = `${softRouteURL}-_-${adobeAudience}`;
            }
        }

        if (softRouteURL) {
            if (softRoute) {
                redirectTag = <Link to={softRouteURL}>{imgTag}</Link>;
            } else {
                redirectTag = (
                    <a
                        data-plcc-id={plccId}
                        data-plcc-name={plccName}
                        href={softRouteURL}
                        target={target}
                        aria-label={ariaLabel}
                    >
                        {imgTag}
                    </a>
                );
            }
        } else {
            redirectTag = <>{imgTag}</>;
        }

        return redirectTag;
    }

    render() {
        const { automationId, bannerClass } = this.props;
        return (
            <div className={dt(['banner', `${bannerClass}`])} data-automation-id={automationId}>
                {this.linkCreation()}
            </div>
        );
    }
}

export default Banner;
